import axios from 'axios';
import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';
import { useAuthStore } from '@shared/stores/authStore';
import { useBrandingStore } from '@shared/stores/brandingStore';
import { env } from '@config/env';
import type { ChannelPreference, ChannelPreferencePatch, Comment, CommentsPage, CommunicationOverview, CommunicationSummary, FeedMessage, FeedPage, FeedScope, FeedView, MessageAction } from './types';

/** Messages fetched per page for the feed's infinite scroll. */
export const FEED_PAGE_SIZE = 20;

/**
 * Two modes, chosen by whether an SSO session is held:
 *  - SSO session present → call the core-api cross-tenant gateway, which aggregates
 *    every linked community (the screenshot's stacked estates).
 *  - No SSO session (direct tenant login) → call the active tenant's regional API
 *    directly and present it as a single section.
 */

interface FeedParams {
  page?: number;
  limit?: number;
  search?: string;
  view?: FeedView;
}

function ssoToken(): string | null {
  return useAuthStore.getState().ssoSessionToken;
}

function gateway() {
  const token = ssoToken();
  if (!token || !env.coreApiUrl) return null;
  return axios.create({
    baseURL: env.coreApiUrl,
    timeout: 20_000,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
}

/**
 * Feed responses are `{ success, data, total, page, limit }` — the page counters sit beside
 * the envelope's `data`, not in the `pagination` object the comment endpoints use.
 */
function toFeedPage(body: unknown): FeedPage {
  const counters = body as { total?: unknown; page?: unknown; limit?: unknown } | null;
  return {
    data: unwrapList<FeedMessage>(body),
    total: Number(counters?.total ?? 0),
    page: Number(counters?.page ?? 1),
    limit: Number(counters?.limit ?? FEED_PAGE_SIZE),
  };
}

async function fetchActiveTenantOverview(): Promise<CommunicationOverview> {
  // Single-tenant: wrap the active community's summary as one section.
  const { data } = await tenantApi.get('/resident/communications/summary');
  const summary = unwrap<CommunicationSummary>(data);
  const name = useBrandingStore.getState().branding?.name || 'My Community';
  return {
    all_unread: summary.all_unread,
    emergency_unread: summary.emergency_unread,
    sections: [
      {
        tenant_id: null,
        name,
        direct_unread: summary.direct_unread,
        channels: summary.channels,
      },
    ],
  };
}

export async function fetchCommunicationOverview(): Promise<CommunicationOverview> {
  const gw = gateway();
  if (gw) {
    const { data } = await gw.get('/portal-auth/communication/summary');
    const overview = unwrap<CommunicationOverview>(data);
    // If cross-tenant fan-out yielded no communities (e.g. every per-tenant call
    // failed), fall back to the active community so the page isn't empty — and any
    // real backend error surfaces instead of a silent "no communities" state.
    if (overview.sections.length > 0) return overview;
    return fetchActiveTenantOverview();
  }
  return fetchActiveTenantOverview();
}

/** All / Emergency — cross-tenant aggregate (gateway) or active-tenant scope feed. */
export async function fetchScopeFeed(scope: Extract<FeedScope, 'all' | 'emergency'>, params: FeedParams = {}): Promise<FeedPage> {
  const gw = gateway();
  if (gw) {
    const { data } = await gw.get(`/portal-auth/communication/${scope}/feed`, { params });
    return toFeedPage(data);
  }
  const { data } = await tenantApi.get(`/resident/communications/${scope}/feed`, { params });
  return toFeedPage(data);
}

export async function fetchDirectFeed(tenantId: string | null, params: FeedParams = {}): Promise<FeedPage> {
  const gw = gateway();
  if (gw && tenantId) {
    const { data } = await gw.get(`/portal-auth/communication/${tenantId}/direct/feed`, { params });
    return toFeedPage(data);
  }
  const { data } = await tenantApi.get('/resident/communications/direct/feed', { params });
  return toFeedPage(data);
}

export async function fetchChannelFeed(tenantId: string | null, channelId: string, params: FeedParams = {}): Promise<FeedPage> {
  const gw = gateway();
  if (gw && tenantId) {
    const { data } = await gw.get(`/portal-auth/communication/${tenantId}/channels/${channelId}/feed`, { params });
    return toFeedPage(data);
  }
  const { data } = await tenantApi.get(`/resident/communications/channels/${channelId}/feed`, { params });
  return toFeedPage(data);
}

export async function fetchChannelPreferences(tenantId: string | null): Promise<ChannelPreference[]> {
  const gw = gateway();
  if (gw && tenantId) {
    const { data } = await gw.get(`/portal-auth/communication/${tenantId}/preferences`);
    return Array.isArray(data?.data) ? data.data : [];
  }
  const { data } = await tenantApi.get('/resident/communications/preferences');
  return Array.isArray(data?.data) ? data.data : [];
}

export async function updateChannelPreference(
  tenantId: string | null,
  channelId: string,
  patch: ChannelPreferencePatch,
): Promise<ChannelPreference> {
  const gw = gateway();
  if (gw && tenantId) {
    const { data } = await gw.put(`/portal-auth/communication/${tenantId}/channels/${channelId}/preference`, patch);
    return data.data;
  }
  const { data } = await tenantApi.put(`/resident/communications/channels/${channelId}/preference`, patch);
  return data.data;
}

/**
 * Short-lived signed download URL for one attachment on one of the resident's own
 * messages. The URL is minted per request by the tenant, which first checks the
 * notification belongs to this resident and that the document is actually on it — the
 * resident never holds a durable link to a document.
 */
export async function fetchAttachmentDownload(
  tenantId: string | null,
  notificationId: string,
  documentId: string,
  disposition: 'attachment' | 'inline' = 'attachment',
): Promise<{ download_url: string; file_name: string }> {
  const path = `/messages/${notificationId}/attachments/${documentId}`;
  const params = { disposition };
  const gw = gateway();
  const { data } = gw && tenantId
    ? await gw.get(`/portal-auth/communication/${tenantId}${path}`, { params })
    : await tenantApi.get(`/resident/communications${path}`, { params });
  if (data?.success === false || !data?.data?.download_url) {
    throw new Error(data?.error ?? 'Attachment not available');
  }
  return data.data;
}

export interface MarkReadInput {
  notification_id?: string;
  channel_id?: string;
  scope?: FeedScope;
}

/** Mark read. tenantId routes the call in SSO mode; null uses the active tenant. */
export async function markCommunicationRead(tenantId: string | null, input: MarkReadInput): Promise<void> {
  const gw = gateway();
  if (gw && tenantId) {
    await gw.post(`/portal-auth/communication/${tenantId}/read`, input);
    return;
  }
  await tenantApi.post('/resident/communications/read', input);
}

/** Comments fetched per page for the resident comment-thread infinite scroll. */
const COMMENTS_PAGE_SIZE = 20;

/** Pull `{ page, pages }` out of the `{ success, data, pagination }` envelope. */
function readCommentsPagination(body: unknown): { page: number; pages: number } {
  const pagination = (body as { pagination?: { page?: number; pages?: number } } | null)?.pagination;
  return { page: pagination?.page ?? 1, pages: pagination?.pages ?? 1 };
}

export async function fetchComments(
  tenantId: string | null,
  channelId: string,
  communicationId: string,
  page = 1,
): Promise<CommentsPage> {
  const params = { page, limit: COMMENTS_PAGE_SIZE };
  const gw = gateway();
  if (gw && tenantId) {
    const { data } = await gw.get(`/portal-auth/communication/${tenantId}/channels/${channelId}/communications/${communicationId}/comments`, { params });
    const cursor = readCommentsPagination(data);
    return { items: Array.isArray(data?.data) ? data.data : [], ...cursor };
  }
  const { data } = await tenantApi.get(`/resident/communications/channels/${channelId}/communications/${communicationId}/comments`, { params });
  const cursor = readCommentsPagination(data);
  return { items: Array.isArray(data?.data) ? data.data : [], ...cursor };
}

export async function postComment(tenantId: string | null, channelId: string, communicationId: string, body: string): Promise<Comment> {
  const gw = gateway();
  if (gw && tenantId) {
    const { data } = await gw.post(`/portal-auth/communication/${tenantId}/channels/${channelId}/communications/${communicationId}/comments`, { body });
    return data.data;
  }
  const { data } = await tenantApi.post(`/resident/communications/channels/${channelId}/communications/${communicationId}/comments`, { body });
  return data.data;
}

export async function deleteComment(tenantId: string | null, commentId: string): Promise<void> {
  const gw = gateway();
  if (gw && tenantId) { await gw.delete(`/portal-auth/communication/${tenantId}/comments/${commentId}`); return; }
  await tenantApi.delete(`/resident/communications/comments/${commentId}`);
}

export async function reportComment(tenantId: string | null, commentId: string, reason?: string): Promise<void> {
  const gw = gateway();
  if (gw && tenantId) { await gw.post(`/portal-auth/communication/${tenantId}/comments/${commentId}/report`, { reason }); return; }
  await tenantApi.post(`/resident/communications/comments/${commentId}/report`, { reason });
}

/**
 * Apply an action to the resident's own messages in one tenant.
 * Returns the number of messages the server actually mutated.
 *
 * Throws on failure. In SSO mode the cross-tenant gateway collapses a failed
 * per-tenant call into an HTTP-200 `{ success: false }` body (so one tenant can't
 * break the aggregate), which axios would NOT treat as an error — so we check the
 * envelope explicitly. This makes `applyMessageActionGrouped`'s allSettled see the
 * same rejection in both direct and SSO modes, rather than silently counting a
 * failed community as "0 messages actioned".
 */
export async function applyMessageAction(
  tenantId: string | null,
  notificationIds: string[],
  action: MessageAction,
): Promise<number> {
  if (notificationIds.length === 0) return 0;
  const payload = { notification_ids: notificationIds, action };
  const gw = gateway();
  const { data } = gw && tenantId
    ? await gw.post(`/portal-auth/communication/${tenantId}/actions`, payload)
    : await tenantApi.post('/resident/communications/actions', payload);
  if (data?.success === false) {
    throw new Error(data?.error ?? 'Failed to apply message action');
  }
  return Number(data?.data?.count ?? 0);
}

export interface GroupedActionResult {
  /** Messages the server confirmed it mutated, summed across tenants. */
  count: number;
  /** Number of communities whose call failed. 0 on full success. */
  failedTenants: number;
  /** True when every community's call failed — the action achieved nothing. */
  allFailed: boolean;
}

/**
 * The aggregated All / Emergency feeds mix messages from several communities, so a
 * selection can span tenants. Group by the message's own tenant_id and issue one
 * call per tenant.
 *
 * A cross-tenant action can partially succeed: one community's call can 500 while
 * another's commits its mutation server-side. This never rejects on a partial
 * failure — it settles every per-tenant call and reports what actually happened via
 * `count` / `failedTenants` / `allFailed`. The caller must inspect the result and
 * surface partial failure honestly (e.g. "archived in 1 of 2 communities") rather
 * than assuming success or showing a blanket failure toast that contradicts the
 * mutations that did land.
 */
export async function applyMessageActionGrouped(
  messages: Array<{ tenant_id?: string | null; notification_id: string }>,
  action: MessageAction,
  fallbackTenantId: string | null,
): Promise<GroupedActionResult> {
  const byTenant = new Map<string | null, string[]>();
  for (const m of messages) {
    const key = m.tenant_id ?? fallbackTenantId ?? null;
    const list = byTenant.get(key) ?? [];
    list.push(m.notification_id);
    byTenant.set(key, list);
  }
  const results = await Promise.allSettled(
    Array.from(byTenant.entries()).map(([tenantId, ids]) => applyMessageAction(tenantId, ids, action)),
  );
  const total = results.length;
  const failedTenants = results.filter((r) => r.status === 'rejected').length;
  const count = results.reduce((sum, r) => (r.status === 'fulfilled' ? sum + r.value : sum), 0);
  return { count, failedTenants, allFailed: total > 0 && failedTenants === total };
}
